import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  TrendingUp,
  BookOpen,
  Beaker,
  Target,
  Flame,
  Calendar,
  BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
    // Track activity when viewing analytics
    api.trackActivity().catch(() => {});
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await api.getProgressAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
    <Card className="rounded-2xl border-0 shadow-md">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{label}</p>
            <p className="text-3xl font-bold text-foreground">{value}</p>
            {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ProgressBar = ({ label, current, max, color }) => {
    const percentage = max > 0 ? Math.round((current / max) * 100) : 0;
    return (
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{label}</span>
          <span className="font-medium">{current}/{max} ({percentage}%)</span>
        </div>
        <div className="h-3 bg-muted rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className={`h-full ${color} rounded-full`}
          />
        </div>
      </div>
    );
  };

  const WeeklyStreak = ({ streak }) => {
    const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    return (
      <div className="flex gap-2 justify-center">
        {days.map((day, i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                i < streak ? 'bg-mint' : 'bg-muted'
              }`}
            >
              {i < streak && <Flame className="w-4 h-4 text-coral" />}
            </div>
            <span className="text-xs text-muted-foreground">{day}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-ocean-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-foreground">Learning Analytics</h1>
                <p className="text-sm text-muted-foreground">Track your founder journey</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={BookOpen}
            label="Lessons Completed"
            value={summary.total_lessons_completed || 0}
            color="gradient-ocean"
            subtext="of 650+ available"
          />
          <StatCard
            icon={Beaker}
            label="Labs Finished"
            value={summary.total_labs_completed || 0}
            color="bg-mint"
            subtext="hands-on exercises"
          />
          <StatCard
            icon={Target}
            label="Readiness Score"
            value={summary.readiness_score || 0}
            color="bg-coral"
            subtext="out of 100"
          />
          <StatCard
            icon={Flame}
            label="Current Streak"
            value={summary.current_streak || 0}
            color="bg-purple-500"
            subtext="days learning"
          />
        </div>

        {/* Weekly Streak */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="rounded-2xl border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-ocean-blue" />
                This Week's Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <WeeklyStreak streak={summary.current_streak || 0} />
              <p className="text-center text-sm text-muted-foreground mt-4">
                {summary.current_streak > 0 
                  ? `🔥 ${summary.current_streak} day streak! Keep it going!`
                  : "Start learning today to build your streak!"}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Progress Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="rounded-2xl border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-ocean-blue" />
                Curriculum Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <ProgressBar
                label="Layer 0: Getting Started"
                current={Math.min(summary.total_lessons_completed || 0, 10)}
                max={10}
                color="gradient-ocean"
              />
              <ProgressBar
                label="Layer 1: Core Skills"
                current={Math.max(0, Math.min((summary.total_lessons_completed || 0) - 10, 47))}
                max={47}
                color="bg-coral"
              />
              <ProgressBar
                label="Labs Completed"
                current={summary.total_labs_completed || 0}
                max={35}
                color="bg-mint"
              />
              <ProgressBar
                label="Modules Mastered"
                current={summary.total_modules_completed || 0}
                max={65}
                color="bg-purple-500"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Readiness Score Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="rounded-2xl border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-ocean-blue" />
                Founder Readiness Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-8">
                <div className="relative">
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="12"
                      className="text-muted"
                    />
                    <motion.circle
                      cx="80"
                      cy="80"
                      r="70"
                      fill="none"
                      stroke="url(#gradient)"
                      strokeWidth="12"
                      strokeLinecap="round"
                      initial={{ strokeDasharray: "0 440" }}
                      animate={{ 
                        strokeDasharray: `${(summary.readiness_score || 0) * 4.4} 440` 
                      }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#1a3a52" />
                        <stop offset="100%" stopColor="#4ecdc4" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-foreground">
                      {summary.readiness_score || 0}
                    </span>
                    <span className="text-sm text-muted-foreground">out of 100</span>
                  </div>
                </div>
              </div>
              <p className="text-center text-muted-foreground">
                {(summary.readiness_score || 0) < 30 && "You're just getting started! Keep learning to boost your score."}
                {(summary.readiness_score || 0) >= 30 && (summary.readiness_score || 0) < 60 && "Good progress! You're building a solid foundation."}
                {(summary.readiness_score || 0) >= 60 && (summary.readiness_score || 0) < 80 && "Impressive! You're well on your way to becoming investor-ready."}
                {(summary.readiness_score || 0) >= 80 && "Outstanding! You're ready to take on the startup world! 🚀"}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default AnalyticsPage;
